import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    IconButton,
    TextField,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    Collapse,
    Autocomplete,
    Popper,
    Portal,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { toast } from 'react-toastify';
import DropzoneArea from '../DropzoneArea';
import Constants from '../../../../assests/Constants/constants';
import fieldStyles from '../../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import styles from './MoveinDatImportModal.module.scss';

const TAB_UPLOAD = 'upload';
const TAB_PREVIEW = 'preview';

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

const MoveinDatImportModal = ({
    open,
    onClose,
    caseId,
    token,
    onSuccess,
    previewMovinDat,
    commitMovinDat,
    t,
    direction = 'rtl',
    sortCodeOptions = [],
    accountCardOptions = [],
    fetchEnums,
    createMissingSortCodes,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    Constants: ConstantsProp,
    journalEntryType = null,
    requireBalanced = false,
    title,
}) => {
    const ConstantsRef = ConstantsProp || Constants;
    const [tab, setTab] = useState(TAB_UPLOAD);
    const [file, setFile] = useState(null);
    const [prmFile, setPrmFile] = useState(null);
    const [amountDivisor, setAmountDivisor] = useState(1);
    const [reverseDescription, setReverseDescription] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [movinDatEditableMissing, setMovinDatEditableMissing] = useState([]);
    const [movinDatSelectedRows, setMovinDatSelectedRows] = useState([]);
    const [movinDatBulkVatType, setMovinDatBulkVatType] = useState(null);
    const [movinDatBulkGroup, setMovinDatBulkGroup] = useState(null);
    const [movinDatBulkPar, setMovinDatBulkPar] = useState(null);
    const [movinDatBulkVat, setMovinDatBulkVat] = useState(null);
    const [movinDatShowMissingSection, setMovinDatShowMissingSection] = useState(false);
    const [orderBy, setOrderBy] = useState('lineNumber');
    const [order, setOrder] = useState('asc');

    const textAlign = direction === 'ltr' ? 'left' : 'right';
    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const handleDrop = (files) => {
        if (files?.length) setFile(files[0]);
        setPreviewData(null);
        setMovinDatEditableMissing([]);
        setMovinDatShowMissingSection(false);
    };

    const handlePrmDrop = (files) => {
        if (files?.length) setPrmFile(files[0]);
        else setPrmFile(null);
        setPreviewData(null);
        setMovinDatEditableMissing([]);
        setMovinDatShowMissingSection(false);
    };

    const normalizeMovinMissingSortCodes = useCallback((missing) => (
        (missing ?? []).map((sc) => ({
            sortCodeId: sc.sortCodeId ?? sc.SortCodeId ?? '',
            sortCodeName: sc.sortCodeName ?? sc.SortCodeName ?? '',
            sortCodeVatType: sc.sortCodeVatType ?? sc.SortCodeVatType ?? null,
            sortCodeGroup: sc.sortCodeGroup ?? sc.SortCodeGroup ?? null,
            participatesInProfitAndLoss:
                typeof sc.participatesInProfitAndLoss === 'boolean'
                    ? sc.participatesInProfitAndLoss
                    : typeof sc.ParticipatesInProfitAndLoss === 'boolean'
                      ? sc.ParticipatesInProfitAndLoss
                      : null,
            vat: typeof sc.vat === 'number' ? sc.vat : typeof sc.Vat === 'number' ? sc.Vat : null,
        }))
    ), []);

    const sortCodeIdToName = useMemo(() => {
        const m = new Map();
        (sortCodeOptions || []).forEach((o) => {
            const id = String(o.sortCodeId ?? o.value ?? '').trim();
            if (id) m.set(id, String(o.sortCodeName ?? o.name ?? o.label ?? '').trim() || id);
        });
        return m;
    }, [sortCodeOptions]);

    const cardIdToName = useMemo(() => {
        const m = new Map();
        (accountCardOptions || [])
            .filter((o) => o.value1 !== 'AddItem')
            .forEach((o) => {
                const id = o.cardId ?? o.value1 ?? o.value;
                if (id == null || id === '') return;
                const strId = String(id).trim();
                const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
                m.set(strId, name);
                if (/^\d+$/.test(strId)) m.set(Number(strId), name);
            });
        return m;
    }, [accountCardOptions]);

    const fetchEnumsRef = useRef(fetchEnums);
    fetchEnumsRef.current = fetchEnums;
    useEffect(() => {
        const isEmpty = !accountCardOptions || accountCardOptions.length === 0;
        if (open && caseId && token && isEmpty && typeof fetchEnumsRef.current === 'function') {
            fetchEnumsRef.current();
        }
    }, [open, caseId, token, accountCardOptions]);

    const handlePreview = async () => {
        if (!file || !caseId || !token || !previewMovinDat) return;
        try {
            setProcessing(true);
            const res = await previewMovinDat(caseId, file, token, amountDivisor, undefined, prmFile || undefined, reverseDescription);
            const entries = res.entries ?? [];
            setPreviewData({
                entries,
                errorMessages: res.errorMessages ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
            });
            setPage(0);
            setMovinDatEditableMissing(normalizeMovinMissingSortCodes(res.missingSortCodes));
            setMovinDatSelectedRows([]);
            setMovinDatShowMissingSection(false);
        } catch (err) {
            console.error('Movein.dat preview failed:', err);
            setPreviewData({
                entries: [],
                errorMessages: [err.message || 'Preview failed'],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
            });
            setMovinDatEditableMissing([]);
        } finally {
            setProcessing(false);
            setTab(TAB_PREVIEW);
        }
    };

    const handleCommit = async () => {
        if (!file || !caseId || !token || !commitMovinDat) return;
        try {
            setProcessing(true);
            if (createMissingSortCodes && movinDatEditableMissing.length > 0) {
                await createMissingSortCodes(movinDatEditableMissing);
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
            const res = await commitMovinDat(caseId, file, token, amountDivisor, undefined, prmFile || undefined, reverseDescription, journalEntryType);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const messages = res?.errorMessages ?? [];
            if (errors > 0 && messages.length > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}, ${errors} ${t('Errors') || 'errors'}.`);
                onSuccess?.();
                handleClose();
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}, ${errors} ${t('Errors') || 'errors'}.`);
                onSuccess?.();
                handleClose();
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
                onSuccess?.();
                handleClose();
            }
        } catch (err) {
            console.error('Movein.dat commit failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = useCallback(() => {
        setTab(TAB_UPLOAD);
        setFile(null);
        setPrmFile(null);
        setPreviewData(null);
        setPage(0);
        setRowsPerPage(10);
        setMovinDatEditableMissing([]);
        setMovinDatSelectedRows([]);
        setMovinDatShowMissingSection(false);
        onClose();
    }, [onClose]);

    const updateMovinDatMissing = useCallback((index, field, value) => {
        setMovinDatEditableMissing((prev) => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });
    }, []);

    const applyMovinDatBulk = useCallback(() => {
        setMovinDatEditableMissing((prev) =>
            prev.map((r) =>
                movinDatSelectedRows.includes(r.sortCodeId)
                    ? {
                          ...r,
                          sortCodeVatType: movinDatBulkVatType?.value ?? r.sortCodeVatType,
                          sortCodeGroup: movinDatBulkGroup?.value ?? r.sortCodeGroup,
                          participatesInProfitAndLoss: movinDatBulkPar?.value ?? r.participatesInProfitAndLoss,
                          vat: movinDatBulkVat?.value ?? r.vat,
                      }
                    : r
            )
        );
        setMovinDatSelectedRows([]);
        setMovinDatBulkVatType(null);
        setMovinDatBulkGroup(null);
        setMovinDatBulkPar(null);
        setMovinDatBulkVat(null);
    }, [movinDatSelectedRows, movinDatBulkVatType, movinDatBulkGroup, movinDatBulkPar, movinDatBulkVat]);

    const movinDatMissingIncomplete = useMemo(
        () =>
            movinDatEditableMissing.some(
                (sc) =>
                    sc.sortCodeVatType === null ||
                    sc.sortCodeVatType === undefined ||
                    sc.participatesInProfitAndLoss === null ||
                    sc.participatesInProfitAndLoss === undefined ||
                    sc.vat === null ||
                    sc.vat === undefined
            ),
        [movinDatEditableMissing]
    );

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const entries = previewData?.entries ?? [];
    const getComparator = (order, orderBy) => (a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        const isNum = (v) => v != null && !Number.isNaN(Number(v));
        if (order === 'asc') {
            if (isNum(aVal) && isNum(bVal)) return Number(aVal) - Number(bVal);
            return String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
        }
        if (isNum(aVal) && isNum(bVal)) return Number(bVal) - Number(aVal);
        return String(bVal ?? '').localeCompare(String(aVal ?? ''), undefined, { numeric: true });
    };
    const sortedEntries = useMemo(() => {
        if (!entries.length) return [];
        return [...entries].sort(getComparator(order, orderBy));
    }, [entries, order, orderBy]);

    const previewSummaries = useMemo(() => {
        const valid = entries.filter((e) => e.isValid);
        const sumAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const hasPrm = valid.some((e) => e.gross != null || e.net != null || e.vat != null);
        const sumGross = hasPrm ? valid.reduce((s, e) => s + (Number(e.gross) ?? Number(e.amount) ?? 0), 0) : null;
        const sumNet = hasPrm ? valid.reduce((s, e) => s + (Number(e.net) ?? 0), 0) : null;
        const sumVat = hasPrm ? valid.reduce((s, e) => s + (Number(e.vat) ?? 0), 0) : null;
        const hasAccount = (v) => v != null && String(v).trim() !== '';
        const round2 = (n) => Math.round(n * 100) / 100;
        const totalDebit = round2(valid.reduce((s, e) => s + (hasAccount(e.debitAccount) ? Number(e.amount) || 0 : 0), 0));
        const totalCredit = round2(valid.reduce((s, e) => s + (hasAccount(e.creditAccount) ? Number(e.amount) || 0 : 0), 0));
        const balanceDiff = round2(totalDebit - totalCredit);
        return { sumAmount, sumGross, sumNet, sumVat, hasPrm, totalDebit, totalCredit, balanceDiff };
    }, [entries]);

    const vatPercentageOptions = useMemo(
        () => (ConstantsRef?.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v })),
        [ConstantsRef]
    );
    const yesNoOptions = useMemo(
        () => [
            { label: t('Yes'), value: true },
            { label: t('No'), value: false },
        ],
        [t]
    );

    const isUnbalanced = requireBalanced && previewSummaries.balanceDiff !== 0;

    if (!open) return null;

    const activeTab = previewData ? tab : TAB_UPLOAD;
    const isPreviewTab = activeTab === TAB_PREVIEW;
    const importDisabled =
        !previewData ||
        processing ||
        (previewData?.validCount ?? 0) === 0 ||
        (movinDatEditableMissing.length > 0 && movinDatMissingIncomplete) ||
        isUnbalanced;

    const paginatedEntries = sortedEntries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0);
    };

    const renderSortHeader = (id, label) => (
        <TableCell align={textAlign}>
            <TableSortLabel
                active={orderBy === id}
                direction={orderBy === id ? order : 'asc'}
                onClick={() => handleRequestSort(id)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const renderCombo = ({ options, value, onChange, placeholder, required = false, className }) => (
        <Autocomplete
            className={`${fieldStyles.combo}${className ? ` ${className}` : ''}`}
            size="small"
            fullWidth
            options={options || []}
            getOptionLabel={(option) => option?.label ?? ''}
            value={value}
            onChange={(e, newValue) => onChange(newValue)}
            isOptionEqualToValue={(a, b) => a?.value === b?.value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="standard"
                    fullWidth
                    placeholder={placeholder}
                    className={`${fieldStyles.styleInput}${required ? ` ${fieldStyles.styleInputRequired}` : ''}`}
                    inputProps={{ ...params.inputProps, style: { textAlign } }}
                    InputProps={{ ...params.InputProps, ...comboInputProps }}
                />
            )}
            ListboxProps={{ direction }}
            PopperComponent={renderComboPopper}
        />
    );

    const cardLabel = (account) => {
        if (account == null || account === '') return account ?? '—';
        const name = cardIdToName.get(String(account).trim()) ?? cardIdToName.get(Number(account));
        return name ? `${name} (${account})` : account;
    };

    const renderUploadTab = () => (
        <>
            <div className={styles.section}>
                <p className={modalStyles.sectionTitle}>movein.dat/movein.doc</p>
                <div className={styles.dropzone}>
                    <DropzoneArea
                        key={file ? file.name : 'movin-empty'}
                        onDrop={handleDrop}
                        accept=".dat"
                    />
                </div>
                {file && (
                    <span className={styles.fileChip}>
                        <InsertDriveFileOutlinedIcon fontSize="inherit" />
                        {t('FileSelected')}: {file.name}
                    </span>
                )}
            </div>

            <div className={styles.section}>
                <p className={modalStyles.sectionTitle}>MOVEIN.PRM {t('Optional') || '(אופציונלי)'}</p>
                <p className={modalStyles.muted}>
                    {t('MoveinPrmHelp') || 'קובץ PRM אופציונלי – מגדיר את מיקומי העמודות ב-DAT. אם לא נבחר, ייעשה שימוש בפורמט Standard/Alternate.'}
                </p>
                <div className={styles.dropzone}>
                    <DropzoneArea
                        key={prmFile ? prmFile.name : 'prm-empty'}
                        onDrop={handlePrmDrop}
                        accept=".prm"
                    />
                </div>
                {prmFile && (
                    <span className={styles.fileChip}>
                        <InsertDriveFileOutlinedIcon fontSize="inherit" />
                        {t('FileSelected')}: {prmFile.name}
                    </span>
                )}
            </div>

            <div className={styles.section}>
                <p className={modalStyles.sectionTitle}>{t('MoveinDatAmountDivisor')}</p>
                <p className={modalStyles.muted}>{t('MoveinDatAmountDivisorHelp')}</p>
                <div className={styles.pills} role="radiogroup">
                    {[
                        { value: 100, label: t('MoveinDatAmountDivisor100') },
                        { value: 10, label: t('MoveinDatAmountDivisor10') },
                        { value: 1, label: t('MoveinDatAmountDivisor1') },
                    ].map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={amountDivisor === opt.value}
                            className={`${modalStyles.tab}${amountDivisor === opt.value ? ` ${modalStyles.tabActive}` : ''}`}
                            onClick={() => {
                                setAmountDivisor(opt.value);
                                setPreviewData(null);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <label className={styles.checkboxRow}>
                <Checkbox
                    checked={reverseDescription}
                    onChange={(e) => setReverseDescription(e.target.checked)}
                    size="small"
                    sx={{ p: 0.5 }}
                />
                {t('MoveinReverseDescription') || 'Reverse Hebrew description (description in file is stored RTL)'}
            </label>
        </>
    );

    const renderMissingSortCodes = () => (
        <div className={`${styles.notice}${movinDatMissingIncomplete ? ` ${styles.noticeWarning}` : ''}`}>
            <div className={styles.missingHeader}>
                <span>
                    {t('MissingSortCodes')}: <strong>{movinDatEditableMissing.length}</strong>
                    {movinDatMissingIncomplete && <> – {t('FillRequiredFields')}</>}
                </span>
                <button
                    type="button"
                    className={modalStyles.ghostBtn}
                    onClick={() => setMovinDatShowMissingSection((prev) => !prev)}
                >
                    {movinDatShowMissingSection ? t('Hide') || 'Hide' : t('Open') || 'Open'}
                    {movinDatShowMissingSection ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </button>
            </div>
            <Collapse in={movinDatShowMissingSection}>
                <div className={styles.bulkRow}>
                    <div className={styles.bulkField}>
                        {renderCombo({
                            options: formattedSortCodeVatType,
                            value: movinDatBulkVatType,
                            onChange: setMovinDatBulkVatType,
                            placeholder: t('SelectSortCodeVatType'),
                        })}
                    </div>
                    <div className={styles.bulkField}>
                        {renderCombo({
                            options: formattedSortCodesGroupNamesOptions,
                            value: movinDatBulkGroup,
                            onChange: setMovinDatBulkGroup,
                            placeholder: t('SelectSortCodeGroupName'),
                        })}
                    </div>
                    <div className={styles.bulkField}>
                        {renderCombo({
                            options: yesNoOptions,
                            value: movinDatBulkPar,
                            onChange: setMovinDatBulkPar,
                            placeholder: t('participatesInProfitAndLoss'),
                        })}
                    </div>
                    <div className={`${styles.bulkField} ${styles.bulkFieldSmall}`}>
                        {renderCombo({
                            options: vatPercentageOptions,
                            value: movinDatBulkVat,
                            onChange: setMovinDatBulkVat,
                            placeholder: t('VAT'),
                        })}
                    </div>
                    <button
                        type="button"
                        className={modalStyles.secondaryBtn}
                        disabled={!movinDatBulkVatType && !movinDatBulkGroup && !movinDatBulkPar && !movinDatBulkVat}
                        onClick={applyMovinDatBulk}
                    >
                        {t('ApplyToSelected')}
                    </button>
                </div>
                <div className={modalStyles.tableWrap}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small" stickyHeader dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={modalStyles.actionsCell} sx={{ width: 48 }}>
                                        <Checkbox
                                            size="small"
                                            checked={
                                                movinDatSelectedRows.length === movinDatEditableMissing.length &&
                                                movinDatEditableMissing.length > 0
                                            }
                                            onChange={(e) =>
                                                setMovinDatSelectedRows(
                                                    e.target.checked ? movinDatEditableMissing.map((r) => r.sortCodeId) : []
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{t('SortCode Id')}</TableCell>
                                    <TableCell>{t('SortCode Name')}</TableCell>
                                    <TableCell>{t('SelectSortCodeVatType')}</TableCell>
                                    <TableCell>{t('SelectSortCodeGroupName')}</TableCell>
                                    <TableCell>{t('participatesInProfitAndLoss')}</TableCell>
                                    <TableCell>{t('VAT')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {movinDatEditableMissing.map((sc, i) => (
                                    <TableRow key={sc.sortCodeId} hover>
                                        <TableCell className={modalStyles.actionsCell}>
                                            <Checkbox
                                                size="small"
                                                checked={movinDatSelectedRows.includes(sc.sortCodeId)}
                                                onChange={(e) =>
                                                    setMovinDatSelectedRows((prev) =>
                                                        e.target.checked
                                                            ? [...prev, sc.sortCodeId]
                                                            : prev.filter((id) => id !== sc.sortCodeId)
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{sc.sortCodeId}</TableCell>
                                        <TableCell>{sc.sortCodeName ?? ''}</TableCell>
                                        <TableCell sx={{ minWidth: 170 }}>
                                            {renderCombo({
                                                options: formattedSortCodeVatType,
                                                value: formattedSortCodeVatType.find(
                                                    (opt) => opt.value === sc.sortCodeVatType?.toString()?.trim()
                                                ) || null,
                                                onChange: (v) => updateMovinDatMissing(i, 'sortCodeVatType', v?.value ?? null),
                                                placeholder: t('SelectSortCodeVatType'),
                                                required: sc.sortCodeVatType == null,
                                            })}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 170 }}>
                                            {renderCombo({
                                                options: formattedSortCodesGroupNamesOptions,
                                                value: formattedSortCodesGroupNamesOptions.find((opt) => opt.value === sc.sortCodeGroup) || null,
                                                onChange: (v) => updateMovinDatMissing(i, 'sortCodeGroup', v?.value ?? null),
                                                placeholder: t('SelectSortCodeGroupName'),
                                            })}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 130 }}>
                                            {renderCombo({
                                                options: yesNoOptions,
                                                value: yesNoOptions.find((opt) => opt.value === sc.participatesInProfitAndLoss) || null,
                                                onChange: (v) => updateMovinDatMissing(i, 'participatesInProfitAndLoss', v?.value ?? null),
                                                placeholder: t('participatesInProfitAndLoss'),
                                                required: sc.participatesInProfitAndLoss == null,
                                            })}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 110 }}>
                                            {renderCombo({
                                                options: vatPercentageOptions,
                                                value: vatPercentageOptions.find((opt) => opt.value === sc.vat) || null,
                                                onChange: (v) => updateMovinDatMissing(i, 'vat', v?.value ?? null),
                                                placeholder: t('VAT'),
                                                required: sc.vat == null,
                                            })}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </Collapse>
        </div>
    );

    const renderPreviewTab = () => {
        const showPrmColumns = entries.some((row) => row.gross != null || row.net != null || row.vat != null);
        return (
            <>
                <div className={styles.stats}>
                    <span className={styles.stat}>{t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong></span>
                    <span className={styles.stat}>{t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong></span>
                    <span className={styles.stat}>{t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong></span>
                    <span className={styles.stat}>{t('SumAmount') || 'Sum Amount'}: <strong>{formatAmount(previewSummaries.sumAmount)}</strong></span>
                    {previewSummaries.hasPrm && previewSummaries.sumGross != null && (
                        <span className={styles.stat}>{t('MoveinGross') || 'Gross'}: <strong>{formatAmount(previewSummaries.sumGross)}</strong></span>
                    )}
                    {previewSummaries.hasPrm && previewSummaries.sumNet != null && (
                        <span className={styles.stat}>{t('MoveinNet') || 'Net'}: <strong>{formatAmount(previewSummaries.sumNet)}</strong></span>
                    )}
                    {previewSummaries.hasPrm && previewSummaries.sumVat != null && (
                        <span className={styles.stat}>{t('MoveinVat') || 'VAT'}: <strong>{formatAmount(previewSummaries.sumVat)}</strong></span>
                    )}
                </div>

                {requireBalanced && (
                    <div className={`${styles.balance}${isUnbalanced ? ` ${styles.balanceError}` : ''}`}>
                        <span>{t('TotalDebit') || 'Total debit'}: {formatAmount(previewSummaries.totalDebit)}</span>
                        <span>{t('TotalCredit') || 'Total credit'}: {formatAmount(previewSummaries.totalCredit)}</span>
                        <span>{t('Difference') || 'Difference'}: {formatAmount(previewSummaries.balanceDiff)}</span>
                        {isUnbalanced && <span>{t('EntryNotBalanced') || 'The entry is not balanced'}</span>}
                    </div>
                )}

                <p className={modalStyles.fieldHint} style={{ marginBottom: 12 }}>
                    {t('MoveinImportCountNote') || 'If import creates fewer entries than preview, some rows may be duplicates (already in system), failed validation, or trailer lines (all 9s) and are listed in errors.'}
                </p>

                {movinDatEditableMissing.length > 0 && renderMissingSortCodes()}

                {movinDatEditableMissing.length === 0 && entries.some((row) => row.sortCode) && (
                    <div className={`${styles.notice} ${styles.noticeSuccess}`}>
                        ✓ {t('NoMissingSortCodes') || 'No missing sort codes.'}
                    </div>
                )}

                {previewData.errorMessages?.length > 0 && (
                    <div className={`${styles.notice} ${styles.noticeError}`}>
                        <strong>{t('Errors') || 'Errors'} ({previewData.errorMessages.length})</strong>
                        <ul>
                            {previewData.errorMessages.map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                    </div>
                )}

                {entries.length > 0 ? (
                    <div className={modalStyles.tableWrap}>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                            <Table size="small" stickyHeader dir={direction}>
                                <TableHead>
                                    <TableRow>
                                        {renderSortHeader('lineNumber', t('Line') || 'Line')}
                                        {renderSortHeader('documentDate', t('DocumentDate') || 'Date')}
                                        {renderSortHeader('reportingMonth', t('ReportingMonth'))}
                                        {renderSortHeader('registrationMonth', t('RegistrationMonth'))}
                                        {renderSortHeader('reference', t('Reference'))}
                                        {showPrmColumns && renderSortHeader('sortCode', t('MoveinSortCode') || 'קוד סוג')}
                                        {renderSortHeader('debitAccount', t('DebitAccount') || 'Debit')}
                                        {renderSortHeader('creditAccount', t('CreditAccount') || 'Credit')}
                                        {renderSortHeader('amount', t('Amount'))}
                                        {showPrmColumns && (
                                            <>
                                                {renderSortHeader('gross', t('MoveinGross') || 'Gross')}
                                                {renderSortHeader('net', t('MoveinNet') || 'Net')}
                                                {renderSortHeader('vat', t('MoveinVat') || 'VAT')}
                                                {renderSortHeader('vatPercentage', t('MoveinVatPct') || 'VAT %')}
                                            </>
                                        )}
                                        {renderSortHeader('description', t('Description'))}
                                        {renderSortHeader('isValid', t('Status') || 'Status')}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedEntries.map((row, i) => (
                                        <TableRow
                                            key={row.lineNumber ?? i}
                                            hover
                                            className={row.isValid ? undefined : styles.rowInvalid}
                                        >
                                            <TableCell>{row.lineNumber}</TableCell>
                                            <TableCell>{row.documentDate}</TableCell>
                                            <TableCell>{row.reportingMonth ?? '—'}</TableCell>
                                            <TableCell>{row.registrationMonth ?? '—'}</TableCell>
                                            <TableCell>{row.reference}</TableCell>
                                            {showPrmColumns && (
                                                <TableCell>
                                                    {row.sortCode != null && row.sortCode !== ''
                                                        ? (sortCodeIdToName.get(String(row.sortCode).trim())
                                                            ? `${sortCodeIdToName.get(String(row.sortCode).trim())} (${row.sortCode})`
                                                            : row.sortCode)
                                                        : '—'}
                                                </TableCell>
                                            )}
                                            <TableCell>{cardLabel(row.debitAccount)}</TableCell>
                                            <TableCell>{cardLabel(row.creditAccount)}</TableCell>
                                            <TableCell>{formatAmount(row.amount)}</TableCell>
                                            {showPrmColumns && (
                                                <>
                                                    <TableCell>{formatAmount(row.gross)}</TableCell>
                                                    <TableCell>{formatAmount(row.net)}</TableCell>
                                                    <TableCell>{formatAmount(row.vat)}</TableCell>
                                                    <TableCell>{row.vatPercentage != null ? `${Number(row.vatPercentage)}%` : '—'}</TableCell>
                                                </>
                                            )}
                                            <TableCell className={styles.descCell} title={row.description}>
                                                {row.description ?? ''}
                                            </TableCell>
                                            <TableCell className={styles.statusCell}>
                                                {row.isValid ? (
                                                    <span className={styles.statusOk}>{t('Valid') || 'OK'}</span>
                                                ) : (
                                                    <span className={styles.statusError} title={row.errorMessage}>{row.errorMessage ?? ''}</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={sortedEntries.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                        />
                    </div>
                ) : (
                    <div className={modalStyles.emptyState}>{t('NoData') || 'No data'}</div>
                )}
            </>
        );
    };

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={processing ? undefined : handleClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal}${isPreviewTab ? ` ${modalStyles.modalEdit}` : ''}`}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={handleClose} className={modalStyles.closeIcon} disabled={processing}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{title || `${t('UploadMoveInFile')} – ${t('Preview')} & ${t('Import')}`}</div>
                                <div />
                            </div>

                            <div className={modalStyles.tabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={!isPreviewTab}
                                    className={`${modalStyles.tab}${!isPreviewTab ? ` ${modalStyles.tabActive}` : ''}`}
                                    onClick={() => setTab(TAB_UPLOAD)}
                                    disabled={processing}
                                >
                                    {t('UploadMoveInFile')}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isPreviewTab}
                                    className={`${modalStyles.tab}${isPreviewTab ? ` ${modalStyles.tabActive}` : ''}`}
                                    onClick={() => setTab(TAB_PREVIEW)}
                                    disabled={processing || !previewData}
                                >
                                    {t('Preview')}
                                </button>
                            </div>

                            <div className={modalStyles.scroll}>
                                {isPreviewTab ? renderPreviewTab() : renderUploadTab()}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={handleClose} disabled={processing}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                {isPreviewTab ? (
                                    <>
                                        <button
                                            type="button"
                                            className={modalStyles.ghostBtn}
                                            onClick={() => setTab(TAB_UPLOAD)}
                                            disabled={processing}
                                        >
                                            {t('Back') || 'Back'}
                                        </button>
                                        <button
                                            type="button"
                                            className={modalStyles.primaryBtn}
                                            onClick={handleCommit}
                                            disabled={importDisabled}
                                        >
                                            {processing
                                                ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                                : t('Import')}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        className={modalStyles.primaryBtn}
                                        onClick={handlePreview}
                                        disabled={!file || processing}
                                    >
                                        {processing
                                            ? <CircularProgress className={modalStyles.spinner} size={18} color="inherit" />
                                            : t('Preview')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MoveinDatImportModal;
